import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Copy,
    ChevronDown,
    ChevronUp,
    Clock,
    Zap,
    Filter,
    Link as LinkIcon,
    Database
} from "lucide-react";
import * as Astro from "../../astronomy.js";
import { PLANET_SYMBOLS, PLANETS } from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";

// =============================================================================
// 定数とヘルパー
// =============================================================================

const GET_SYMBOL = (id) => {
    if (id === "AP") return "♈"; // 世界点 (Aries Point)
    return PLANET_SYMBOLS[id] || id;
};

const ASPECT_ICONS = {
    Conjunction: "☌",
    Opposition: "☍",
    Square: "□",
    "Semi-square": "∠",
    Sesquiquadrate: "⚼",
    Quincunx: "ㅈ",
};

const DYNAMIC_PLANETS = [...PLANETS, "ASC", "MC"];
const TRANSIT_OUTER_ALWAYS = ["Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"];
const AP_POINTS = [0, 90, 180, 270];

const SIGN_COLORS_DEFAULT = {
    "♈": "#e74c3c", "♉": "#27ae60", " Gemini": "#f1c40f", "Cancer": "#3498db", "Leo": "#e67e22", "Virgo": "#95a5a6",
    "Libra": "#fd79a8", "Scorpio": "#8e44ad", "Sagittarius": "#2980b9", "Capricorn": "#2c3e50", "Aquarius": "#00cec9", "Pisces": "#74b9ff",
};

// SA Direct / Indirect で優先する天体（金融占星術）
const SA_FINANCIAL_PLANETS = [
    "Sun",      // 基本エネルギー・景気サイクル
    "Moon",     // 大衆心理・投機的マネー
    "Mars",     // 市場の攻撃性・急変
    "Jupiter",  // 拡大・バブル・強気相場
    "Saturn",   // 収縮・規制・弱気相場の天井・底
    "Uranus",   // 突然のショック・テクノロジー株
    "Neptune",  // 混乱・詐欺・バブル崩壊
    "Pluto",    // 構造的変容・大底・大天井
    "MC",       // 企業/国家の評判・頂点
    "ASC",      // チャートの基点
];

// SA Indirect の中点：金融で特に重要なペアを拡充
const FINANCIAL_MIDPOINTS = [
    { p1: "Jupiter", p2: "Saturn" },   // 景気サイクルの象徴（20年周期）
    { p1: "Jupiter", p2: "Uranus" },   // 突発的な利益・バブル
    { p1: "Jupiter", p2: "Pluto" },    // 富の集中・大相場
    { p1: "Saturn",  p2: "Uranus" },   // 旧体制の崩壊・規制変革
    { p1: "Saturn",  p2: "Neptune" },  // 幻想の崩壊・流動性危機
    { p1: "Saturn",  p2: "Pluto" },    // 強制的な再構築・暴落
    { p1: "Uranus",  p2: "Neptune" },  // 金融システムの変革
    { p1: "Uranus",  p2: "Pluto" },    // 革命的変動
    { p1: "Sun",     p2: "Moon" },     // 市場の基本センチメント
    { p1: "Mars",    p2: "Saturn" },   // 損失・急落
    { p1: "Mars",    p2: "Pluto" },    // 激烈な動き・クラッシュ
];

// トランジット：常時計算
const TRANSIT_FINANCIAL_ALWAYS = [
    "Jupiter",   // 拡大・強気
    "Saturn",    // 収縮・弱気・変曲点
    "Uranus",    // ショック・ボラティリティ急増
    "Neptune",   // 混乱・流動性
    "Pluto",     // 構造転換
    "Mars",      // 常時計算に格上げ
];

// SA Indirect のアスペクト修正
const INDIRECT_ASPECT_ANGLES = [0, 90, 45, 135];

// Echo判定のタイムウィンドウ
const ECHO_WINDOWS = {
    'SA-TR': 60,   // SAの有効期間を考慮
    'SA-PR': 45,   // SA×ProgrMoon
    'TR-PR': 15,   // TR×ProgrMoon（共に高速）
};

// Echoスコアの重み付け
const ECHO_WEIGHT = {
    'SA-TR': 3,    // 最重要
    'SA-PR': 2,
    'TR-PR': 1,
};

// ソーラーアーク予測で使用するアスペクト定義 (6種類に限定)
const SA_ASPECT_DEFS = [
    { name: "Conjunction", angle: 0, symbol: "☌", type: "Hard" },
    { name: "Opposition", angle: 180, symbol: "☍", type: "Hard" },
    { name: "Square", angle: 90, symbol: "□", type: "Hard" },
    { name: "Semi-square", angle: 45, symbol: "∠", type: "Hard" },
    { name: "Sesquiquadrate", angle: 135, symbol: "⚼", type: "Hard" },
    { name: "Quincunx", angle: 150, symbol: "ㅈ", type: "Hard" },
];

// =============================================================================
// サブコンポーネント: ヒットアイテムの表示
// =============================================================================

const PredictHitItem = (props) => {
    const { type, p1, asp, p2, p3, isIndirect, isDarkMode, planetColors, signColors, rx1, rx2, isMobile } = props;

    const renderPlanetIcon = (id, labelType, isRetro) => {
        let displayId = id;
        let prefix = labelType;
        if (id.startsWith("SA_")) {
            displayId = id.replace("SA_", "");
            prefix = "S";
        }

        const isAP = displayId === "AP";

        // ★修正: AP (Aries Point) の場合、背景なし・星座色のみのスタイルにする
        if (isAP) {
            const ariesColor = signColors ? (Array.isArray(signColors) ? signColors[0] : (signColors["♈"] || signColors["Aries"])) : SIGN_COLORS_DEFAULT["♈"];
            return (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "2px", margin: "0 4px" }}>
                    <span style={{ fontSize: "10px", color: isDarkMode ? "#aaa" : "#888", fontWeight: "normal", marginRight: "2px" }}>{prefix}</span>
                    <span style={{
                        fontSize: "16px",
                        color: ariesColor,
                        lineHeight: 1,
                        fontWeight: "normal",
                        marginRight: "4px"
                    }}>
                        {"♈\uFE0E"}
                    </span>
                    <span style={{ fontSize: "12px", color: isDarkMode ? "#fff" : "#333", fontWeight: "bold" }}>AP</span>
                </div>
            );
        }

        const baseColor = planetColors[displayId] || (isDarkMode ? "#fff" : "#333");
        const symbol = GET_SYMBOL(displayId);

        return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: "2px", margin: "0 4px" }}>
                <span style={{ fontSize: "10px", color: isDarkMode ? "#aaa" : "#888", fontWeight: "normal", marginRight: "2px" }}>{prefix}</span>
                <div style={{
                    width: "20px", height: "20px", borderRadius: "50%", background: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)", marginRight: "4px",
                    border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #eee"
                }}>
                    <span style={{ fontSize: "14px", color: baseColor, width: "100%", textAlign: "center" }}>{symbol}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
                    <span style={{ fontSize: "12px", color: isDarkMode ? "#fff" : "#333", fontWeight: "bold" }}>{displayId}</span>
                    {isRetro && (
                        <span style={{ fontSize: "8px", color: "#ff4d4f", fontWeight: "900" }}>Rx</span>
                    )}
                </div>
            </div>
        );
    };

    if (isIndirect) {
        return (
            <div style={{ display: "flex", alignItems: "center", fontSize: "12px", color: isDarkMode ? "#fff" : "#333", padding: "4px 0" }}>
                {renderPlanetIcon(p2, "N")}
                <span style={{ margin: "0 4px" }}>/</span>
                {renderPlanetIcon(p3, "N")}
                <span style={{ margin: "0 4px" }}>=</span>
                {renderPlanetIcon(p1, "S", rx1)}
                <span style={{ marginLeft: "8px", fontSize: "10px", color: "#888" }}>(Indirect{asp ? ` ${asp}` : ""})</span>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", alignItems: "center", fontSize: "12px", color: isDarkMode ? "#fff" : "#333", padding: "4px 0" }}>
            {renderPlanetIcon(p1, type, rx1)}
            <div style={{
                margin: "0 8px", display: "flex", alignItems: "center", gap: "4px",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                padding: "2px 8px", borderRadius: "4px", border: "1px solid " + (isDarkMode ? "#334155" : "#edf2f7")
            }}>
                <span style={{ fontSize: "16px", color: "#e74c3c" }}>{ASPECT_ICONS[asp] || asp}</span>
                <span style={{ fontSize: "11px", fontWeight: "bold" }}>{asp}</span>
            </div>
            {renderPlanetIcon(p2, "N", rx2)}
                {/* ★修正: トランジット(T)の場合は Direct 表記を出さない */}
                {type === "S" && <span style={{ marginLeft: "8px", fontSize: "10px", color: "#888" }}>(Direct)</span>}
            </div>
        );
    };

// =============================================================================
// メインコンポーネント
// =============================================================================

export default function SolarArcPredictTimeline(props) {
    const {
        natalDate,
        transitDate,
        natalPositions,
        isDarkMode,
        planetColors,
        signColors,
        isMobile,
        aspectSettings
    } = props;

    // 現在のアスペクトグリッド計算
    const currentAspects = useMemo(() => {
        if (!natalDate || !transitDate || !natalPositions) return [];
        const saPos = Astro.getSolarArcPositions(natalDate, transitDate, natalPositions);
        const results = [];
        const saBodies = Object.keys(saPos).filter(p => DYNAMIC_PLANETS.includes(p));
        const natalBodies = Object.keys(natalPositions).filter(p => DYNAMIC_PLANETS.includes(p));

        const orbLimit = 0.5;

        saBodies.forEach(p1 => {
            const lon1 = saPos[p1];
            natalBodies.forEach(p2 => {
                if (p1 === p2) return;
                const lon2 = natalPositions[p2];
                let diff = Math.abs(lon1 - lon2) % 360;
                const dist = diff > 180 ? 360 - diff : diff;

                SA_ASPECT_DEFS.forEach(asp => {
                    const orb = Math.abs(dist - asp.angle);
                    if (orb <= orbLimit) {
                        results.push({
                            p1, p2,
                            name: asp.name,
                            symbol: asp.symbol,
                            type: asp.type,
                            angle: asp.angle,
                            dist,
                            orb
                        });
                    }
                });
            });

            // 対 AP
            const nearestAP = Math.round(lon1 / 90) * 90 % 360;
            let diffAP = Math.abs(lon1 - nearestAP) % 360;
            if (diffAP > 180) diffAP = 360 - diffAP;

            [0, 90, 180].forEach(angle => {
                const orb = Math.abs(diffAP - angle);
                if (orb <= orbLimit) {
                    results.push({
                        p1, p2: 'AP',
                        name: angle === 0 ? 'Conjunction' : (angle === 90 ? 'Square' : 'Opposition'),
                        symbol: angle === 0 ? '☌' : (angle === 90 ? '□' : '☍'),
                        type: 'Hard',
                        angle,
                        dist: diffAP,
                        orb
                    });
                }
            });
        });

        return results.sort((a, b) => a.orb - b.orb);
    }, [natalDate, transitDate, natalPositions, aspectSettings]);

    const formatDMS = (lon, showSeconds = false) => {
        if (lon == null) return showSeconds ? "0°00'00\"" : "0°00'";
        const d = Math.floor(lon);
        const m = Math.floor((lon - d) * 60);
        if (!showSeconds) return `${d}°${m.toString().padStart(2, "0")}'`;
        const s = Math.floor(((lon - d) * 60 - m) * 60);
        return `${d}°${m.toString().padStart(2, "0")}'${s.toString().padStart(2, "0")}"`;
    };

    const getDegInfo = (lon) => {
        if (lon == null) return { sym: "", text: "0°00'00\"" };
        const sign = Astro.getZodiacSign(lon);
        const degInSign = lon % 30;
        const d = Math.floor(degInSign);
        const m = Math.floor((degInSign - d) * 60);
        const s = Math.floor(((degInSign - d) * 60 - m) * 60);
        return {
            sym: sign ? sign.symbol : "",
            text: `${d}°${m.toString().padStart(2, "0")}'${s.toString().padStart(2, "0")}"`
        };
    };

    const renderAspectList = () => {
        const gridLayout = isMobile
            ? "minmax(60px, 1fr) 1fr minmax(60px, 1fr) 0.5fr 0.8fr 0.5fr"
            : "1.2fr 1.3fr 1.2fr 0.7fr 1fr 0.7fr";

        return (
            <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                <div style={Object.assign({}, commonStyles.dynamicBlockTitle(isDarkMode, isMobile), {
                    borderBottom: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                    paddingBottom: "10px",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                })}>
                    <Clock size={18} color={isDarkMode ? "#ffffff" : "#64748b"} />
                    <span>ソーラーアーク（SA）× ネイタル（N）アスペクト</span>
                </div>

                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <div style={{
                        minWidth: isMobile ? "450px" : "100%",
                        display: "flex",
                        flexDirection: "column",
                        background: isDarkMode ? "transparent" : "#fff",
                    }}>
                        {/* Header */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: gridLayout,
                            padding: isMobile ? "10px 8px" : "10px 16px",
                            background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#f8fafc",
                            fontSize: "10px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            borderBottom: "1px solid " + (isDarkMode ? "#334155" : "#f1f5f9")
                        }}>
                            <div>FROM (SA)</div>
                            <div>アスペクト</div>
                            <div>TO (Natal)</div>
                            <div style={{ textAlign: "right" }}>角度</div>
                            <div style={{ textAlign: "right" }}>ARC</div>
                            <div style={{ textAlign: "right" }}>Orb</div>
                        </div>

                        {/* List */}
                        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                            {currentAspects.length === 0 ? (
                                <div style={{ padding: "30px", textAlign: "center", color: "#888", fontSize: "12px" }}>
                                    アスペクトなし
                                </div>
                            ) : (
                                currentAspects.map((item, idx) => {
                                    const p1Color = planetColors[item.p1] || (isDarkMode ? "#fff" : "#333");
                                    const p2Color = planetColors[item.p2] || (isDarkMode ? "#fff" : "#333");
                                    const isTight = item.orb <= 0.2;

                                    return (
                                        <div key={idx} style={{
                                            display: "grid",
                                            gridTemplateColumns: gridLayout,
                                            padding: isMobile ? "8px 8px" : "10px 16px",
                                            background: isTight ? (isDarkMode ? "rgba(245, 158, 11, 0.1)" : "#fff9e6") : "transparent",
                                            alignItems: "center",
                                            fontSize: isMobile ? "11px" : "12px",
                                            borderBottom: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9"),
                                            color: isDarkMode ? "#cbd5e1" : "#1e293b",
                                        }}>
                                            {/* FROM */}
                                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                <span style={{ fontSize: "16px", color: p1Color }}>{GET_SYMBOL(item.p1)}</span>
                                                <span style={{ fontWeight: "bold" }}>{item.p1}</span>
                                            </div>

                                            {/* ASPECT */}
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ fontSize: "18px", color: item.type === "Hard" ? "#ef4444" : "#22c55e" }}>{item.symbol}</span>
                                                <span style={{ fontSize: "11px", color: isDarkMode ? "#94a3b8" : "#64748b" }}>{item.name}</span>
                                            </div>

                                            {/* TO */}
                                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                <span style={{ fontSize: "16px", color: p2Color }}>{GET_SYMBOL(item.p2)}</span>
                                                <span style={{ fontWeight: "bold" }}>{item.p2}</span>
                                            </div>

                                            {/* Angle */}
                                            <div style={{ textAlign: "right", color: isDarkMode ? "#94a3b8" : "#64748b" }}>{item.angle}°</div>

                                            {/* ARC */}
                                            <div style={{ textAlign: "right", fontFamily: "monospace" }}>{formatDMS(item.dist)}</div>

                                            {/* Orb */}
                                            <div style={{ textAlign: "right", fontWeight: isTight ? "bold" : "normal", color: isTight ? "#f59e0b" : "inherit" }}>
                                                {item.orb.toFixed(2)}°
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderAspectMatrix = () => {
        const tablePlanets = PLANETS.slice(0, 10);
        const saPos = Astro.getSolarArcPositions(natalDate, transitDate, natalPositions);

        return (
            <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                <div style={Object.assign({}, commonStyles.dynamicBlockTitle(isDarkMode, isMobile), {
                    borderBottom: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                    paddingBottom: "10px",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                })}>
                    <Filter size={18} color={isDarkMode ? "#ffffff" : "#64748b"} />
                    <span>ソーラーアーク（SA）× ネイタル（N）アスペクト・グリッド</span>
                </div>

                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <table style={{ borderCollapse: "collapse", fontSize: "12px", width: "100%", minWidth: "800px" }}>
                        <thead>
                            <tr>
                                <th style={Object.assign({}, commonStyles.aspectTableCell(false, isDarkMode), {
                                    minWidth: "160px",
                                    background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                })}></th>
                                {tablePlanets.map(p => (
                                    <th key={p} style={Object.assign({}, commonStyles.aspectTableCell(false, isDarkMode), {
                                        color: planetColors[p] || (isDarkMode ? "#fff" : "#333"),
                                        background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                    })}>
                                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center" }}>
                                            {PLANET_SYMBOLS[p]}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tablePlanets.map((p1, i) => {
                                const lon1 = saPos[p1];
                                const degInfo = getDegInfo(lon1);
                                return (
                                    <tr key={p1}>
                                        <td style={Object.assign({}, commonStyles.aspectTableCell(false, isDarkMode), {
                                            fontWeight: "bold", padding: "4px 8px", minWidth: "160px",
                                            background: isDarkMode ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                        })}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-start" }}>
                                                <div style={{ display: "flex", alignItems: "baseline", minWidth: "24px" }}>
                                                    <span style={{ fontSize: "18px", color: planetColors[p1] || (isDarkMode ? "#fff" : "#333") }}>
                                                        {PLANET_SYMBOLS[p1]}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: "13px", color: isDarkMode ? "#ffffff" : "#64748b", width: "55px", fontWeight: "bold" }}>
                                                    {p1}
                                                </span>
                                                <span style={{ fontSize: "16px", color: signColors ? (signColors[degInfo.sym] || "#8b5cf6") : "#8b5cf6", display: "flex", alignItems: "center" }}>
                                                    {degInfo.sym + "\uFE0E"}
                                                </span>
                                                <span style={{ fontSize: "10px", fontWeight: "normal", color: isDarkMode ? "#ffffff" : "#333" }}>
                                                    {degInfo.text}
                                                </span>
                                            </div>
                                        </td>
                                        {tablePlanets.map((p2, j) => {
                                            const lon2 = natalPositions[p2];
                                            let asp = null;
                                            if (lon1 != null && lon2 != null) {
                                                let diff = Math.abs(lon1 - lon2) % 360;
                                                const dist = diff > 180 ? 360 - diff : diff;
                                                SA_ASPECT_DEFS.forEach(d => {
                                                    const orb = Math.abs(dist - d.angle);
                                                    if (orb <= 0.5) {
                                                        if (!asp || orb < asp.orb) asp = { ...d, orb };
                                                    }
                                                });
                                            }

                                            let cellBgColor = commonStyles.aspectTableCell(true, isDarkMode).backgroundColor;
                                            if (asp) {
                                                const isTight = asp.orb <= 0.2;
                                                if (asp.type === "Hard") {
                                                    cellBgColor = isDarkMode
                                                        ? isTight ? "rgba(231, 76, 60, 0.25)" : "rgba(231, 76, 60, 0.15)"
                                                        : isTight ? "#e1f5fe" : "#f1faff";
                                                } else {
                                                    cellBgColor = isDarkMode
                                                        ? isTight ? "rgba(39, 174, 96, 0.25)" : "rgba(39, 174, 96, 0.15)"
                                                        : isTight ? "#ffebee" : "#fff5f5";
                                                }
                                            }

                                            return (
                                                <td key={p1 + "-" + p2} style={Object.assign({}, commonStyles.aspectTableCell(true, isDarkMode), {
                                                    backgroundColor: cellBgColor, padding: "6px 2px", height: "48px", verticalAlign: "middle",
                                                })}>
                                                    {asp && (
                                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                                            <span style={{ color: asp.type === "Hard" ? "#e74c3c" : "#27ae60", fontSize: "16px", lineHeight: "1" }}>
                                                                {asp.symbol}
                                                            </span>
                                                            <span style={{ fontSize: "9px", color: isDarkMode ? "#ffffff" : "#444" }}>
                                                                {asp.orb.toFixed(1)}°
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // 無限ページネーションのための状態
    const [offsetYears, setOffsetYears] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 100;
    const POOL_YEARS = 3; // 1回の計算年数を減らして軽量化

    // データの再計算時にリセット
    useEffect(() => {
        setOffsetYears(0);
        setCurrentPage(1);
    }, [natalDate, transitDate, natalPositions]);

    // 指定年数分のデータを計算
    const timelineData = useMemo(() => {
        if (!natalDate || !transitDate || !natalPositions) return [];

        const baseStartDate = new Date(transitDate);
        const startDate = new Date(baseStartDate.getTime());
        startDate.setFullYear(startDate.getFullYear() + offsetYears);

        // ネイタルから地球を除外
        const natalBodies = Object.keys(natalPositions).filter(p => DYNAMIC_PLANETS.includes(p) && p !== "Earth");

        const midpoints = FINANCIAL_MIDPOINTS.map(mp => {
            if (natalPositions[mp.p1] != null && natalPositions[mp.p2] != null) {
                return {
                    ...mp,
                    lon: Astro.calculateMidpoint(natalPositions[mp.p1], natalPositions[mp.p2]),
                    label: `${mp.p1}/${mp.p2}`
                };
            }
            return null;
        }).filter(Boolean);

        const allCandidates = [];
        const calcDays = POOL_YEARS * 366;

        // ポジションとデータの事前計算（-1日〜calcDays+1日）
        const dayData = [];
        for (let d = -1; d <= calcDays + 1; d++) {
            const curDate = new Date(startDate.getTime() + d * 86400000);
            dayData.push({
                date: curDate,
                saPos: Astro.getSolarArcPositions(natalDate, curDate, natalPositions),
                pMoonLon: Astro.getSecondaryProgressionPositions(natalDate, curDate, null, null).Moon,
                trPos: TRANSIT_FINANCIAL_ALWAYS.reduce((acc, p) => {
                    acc[p] = Astro.getPlanetLongitude(p, curDate);
                    return acc;
                }, {}),
                trRx: TRANSIT_FINANCIAL_ALWAYS.reduce((acc, p) => {
                    acc[p] = Astro.isRetrograde(p, curDate);
                    return acc;
                }, {})
            });
        }

        const transitAspects = [
            { a: 0, n: 'Conjunction' }, { a: 90, n: 'Square' }, { a: 180, n: 'Opposition' },
            { a: 45, n: 'Semi-square' }, { a: 135, n: 'Sesquiquadrate' }
        ];

        // 各日ごとにピーク（極小値）を検出
        for (let d = 0; d < calcDays; d++) {
            const idx = d + 1; // dayData は d=-1 から始まっているため
            const prev = dayData[idx - 1];
            const curr = dayData[idx];
            const next = dayData[idx + 1];

            // 1. SA Direct
            SA_FINANCIAL_PLANETS.forEach(p1 => {
                const sLon = curr.saPos[p1];
                if (sLon == null) return;
                
                natalBodies.forEach(p2 => {
                    const nLon = natalPositions[p2];
                    if (nLon == null) return;
                    
                    const getMinOrb = (data) => {
                        let diff = Math.abs(data.saPos[p1] - nLon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        let minO = 999;
                        [0, 90, 180, 45, 135].forEach(a => {
                            minO = Math.min(minO, Math.abs(diff - a));
                        });
                        return minO;
                    };

                    const orb = getMinOrb(curr);
                    if (orb <= 0.5 && orb < getMinOrb(prev) && orb <= getMinOrb(next)) {
                        let diff = Math.abs(sLon - nLon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        [0, 90, 180, 45, 135].forEach(angle => {
                            if (Math.abs(diff - angle) === orb) {
                                let aspName = angle === 0 ? 'Conjunction' : (angle === 90 ? 'Square' : (angle === 180 ? 'Opposition' : (angle === 45 ? 'Semi-square' : 'Sesquiquadrate')));
                                allCandidates.push({
                                    type: 'SA_Direct', dayIdx: d, date: new Date(curr.date), orb,
                                    p1, p2, target: p2, asp: aspName
                                });
                            }
                        });
                    }
                });

                // 対 世界点 (AP)
                const getMinOrbAP = (data) => {
                    const nearestAPNum = Math.round(data.saPos[p1] / 90) * 90 % 360;
                    let diffAP = Math.abs(data.saPos[p1] - nearestAPNum) % 360;
                    if (diffAP > 180) diffAP = 360 - diffAP;
                    let minO = 999;
                    [0, 90, 180, 45, 135].forEach(a => {
                        minO = Math.min(minO, Math.abs(diffAP - a));
                    });
                    return minO;
                };

                const orbAP = getMinOrbAP(curr);
                if (orbAP <= 0.5 && orbAP < getMinOrbAP(prev) && orbAP <= getMinOrbAP(next)) {
                    const nearestAPNum = Math.round(sLon / 90) * 90 % 360;
                    let diffAP = Math.abs(sLon - nearestAPNum) % 360;
                    if (diffAP > 180) diffAP = 360 - diffAP;
                    [0, 90, 180, 45, 135].forEach(angle => {
                        if (Math.abs(diffAP - angle) === orbAP) {
                            let aspName = angle === 0 ? 'Conjunction' : (angle === 90 ? 'Square' : (angle === 180 ? 'Opposition' : (angle === 45 ? 'Semi-square' : 'Sesquiquadrate')));
                            allCandidates.push({
                                type: 'SA_Direct', dayIdx: d, date: new Date(curr.date), orb: orbAP,
                                p1, p2: 'AP', target: 'AP', asp: aspName
                            });
                        }
                    });
                }
            });

            // 2. SA Indirect
            SA_FINANCIAL_PLANETS.forEach(p1 => {
                midpoints.forEach(mp => {
                    const getMinOrbMP = (data) => {
                        let diff = Math.abs(data.saPos[p1] - mp.lon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        let minO = 999;
                        INDIRECT_ASPECT_ANGLES.forEach(a => {
                            minO = Math.min(minO, Math.abs(diff - a));
                        });
                        return minO;
                    };
                    const orb = getMinOrbMP(curr);
                    if (orb <= 0.5 && orb < getMinOrbMP(prev) && orb <= getMinOrbMP(next)) {
                        let diff = Math.abs(curr.saPos[p1] - mp.lon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        INDIRECT_ASPECT_ANGLES.forEach(angle => {
                            if (Math.abs(diff - angle) === orb) {
                                allCandidates.push({
                                    type: 'SA_Indirect', dayIdx: d, date: new Date(curr.date), orb,
                                    p1, p2: mp.p1, p3: mp.p2, target: mp.label,
                                    isIndirect: true, asp: angle === 0 ? '' : (angle + '°')
                                });
                            }
                        });
                    }
                });
            });

            // 3. Transit
            TRANSIT_FINANCIAL_ALWAYS.forEach(p1 => {
                const tLon = curr.trPos[p1];
                if (tLon == null) return;
                const isAPImportant = (p1 === "Saturn" || p1 === "Pluto") && (Math.abs(tLon % 90) <= 0.5);

                const checkTransit = (lon, nLon, targetName, isAP = false) => {
                    let diff = Math.abs(lon - nLon) % 360;
                    if (diff > 180) diff = 360 - diff;
                    let minO = 999;
                    transitAspects.forEach(asp => {
                        minO = Math.min(minO, Math.abs(diff - asp.a));
                    });
                    return minO;
                };

                // 対 ネイタル天体
                natalBodies.forEach(p2 => {
                    const nLon = natalPositions[p2];
                    const getOrb = (data) => checkTransit(data.trPos[p1], nLon, p2);
                    const orb = getOrb(curr);
                    if (orb <= 0.5 && orb < getOrb(prev) && orb <= getOrb(next)) {
                        let diff = Math.abs(tLon - nLon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        transitAspects.forEach(asp => {
                            if (Math.abs(diff - asp.a) === orb) {
                                allCandidates.push({
                                    type: 'Transit', dayIdx: d, date: new Date(curr.date), orb,
                                    p1, p2, target: p2, asp: asp.n, isRx: !!curr.trRx[p1], isAPImportant
                                });
                            }
                        });
                    }
                });

                // 対 世界点 (AP)
                const getOrbAP = (data) => {
                    const nearestAPNum = Math.round(data.trPos[p1] / 90) * 90 % 360;
                    let diffAP = Math.abs(data.trPos[p1] - nearestAPNum) % 360;
                    if (diffAP > 180) diffAP = 360 - diffAP;
                    let minO = 999;
                    transitAspects.forEach(asp => {
                        minO = Math.min(minO, Math.abs(diffAP - asp.a));
                    });
                    return minO;
                };
                const orbAP = getOrbAP(curr);
                if (orbAP <= 0.5 && orbAP < getOrbAP(prev) && orbAP <= getOrbAP(next)) {
                    const nearestAPNum = Math.round(tLon / 90) * 90 % 360;
                    let diffAP = Math.abs(tLon - nearestAPNum) % 360;
                    if (diffAP > 180) diffAP = 360 - diffAP;
                    transitAspects.forEach(asp => {
                        if (Math.abs(diffAP - asp.a) === orbAP) {
                            allCandidates.push({
                                type: 'Transit', dayIdx: d, date: new Date(curr.date), orb: orbAP,
                                p1, p2: 'AP', target: 'AP', asp: asp.n, isRx: !!curr.trRx[p1], isAPImportant
                            });
                        }
                    });
                }
            });

            // 4. Progressed Moon
            if (curr.pMoonLon != null) {
                natalBodies.forEach(p2 => {
                    const nLon = natalPositions[p2];
                    const getOrb = (moonLon) => {
                        if (moonLon == null) return 999;
                        let diff = Math.abs(moonLon - nLon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        let minO = 999;
                        [0, 90, 180, 45, 135].forEach(a => {
                            minO = Math.min(minO, Math.abs(diff - a));
                        });
                        return minO;
                    };
                    const orb = getOrb(curr.pMoonLon);
                    if (orb <= 0.3 && orb < getOrb(prev.pMoonLon) && orb <= getOrb(next.pMoonLon)) {
                        let diff = Math.abs(curr.pMoonLon - nLon) % 360;
                        if (diff > 180) diff = 360 - diff;
                        [0, 90, 180, 45, 135].forEach(angle => {
                            if (Math.abs(diff - angle) === orb) {
                                let aspName = angle === 0 ? 'Conjunction' : (angle === 90 ? 'Square' : (angle === 180 ? 'Opposition' : (angle === 45 ? 'Semi-square' : 'Sesquiquadrate')));
                                allCandidates.push({
                                    type: 'Progress', dayIdx: d, date: new Date(curr.date), orb,
                                    p1: 'Moon', p2, target: p2, asp: aspName
                                });
                            }
                        });
                    }
                });
            }
        }

        const exactHitsList = [...allCandidates].sort((a, b) => a.date - b.date);

        const finalResultsByDate = {};
        exactHitsList.forEach(hit => {
            const dateStr = `${hit.date.getFullYear()}/${hit.date.getMonth() + 1}/${hit.date.getDate()}`;
            if (!finalResultsByDate[dateStr]) {
                finalResultsByDate[dateStr] = {
                    date: dateStr, dateObj: hit.date,
                    saHits: [], transitHits: [], progressHits: [],
                    hasEcho: false, echoTargets: new Set(), echoScore: 0,
                    isAPCritical: false
                };
            }
            if (hit.type.startsWith('SA_')) finalResultsByDate[dateStr].saHits.push(hit);
            else if (hit.type === 'Transit') {
                finalResultsByDate[dateStr].transitHits.push(hit);
                if (hit.isAPImportant) finalResultsByDate[dateStr].isAPCritical = true;
            }
            else if (hit.type === 'Progress') finalResultsByDate[dateStr].progressHits.push(hit);
        });

        const rows = Object.values(finalResultsByDate).sort((a, b) => a.dateObj - b.dateObj);

        // Echoスコア計算
        rows.forEach(row => {
            const targetScores = {}; // target -> score
            const rowHits = [...row.saHits, ...row.transitHits, ...row.progressHits];
            
            rowHits.forEach(baseHit => {
                const target = baseHit.target;
                const baseType = baseHit.type.startsWith('SA_') ? 'SA' : (baseHit.type === 'Transit' ? 'TR' : 'PR');
                
                exactHitsList.forEach(h => {
                    if (h.target !== target) return;
                    const hType = h.type.startsWith('SA_') ? 'SA' : (h.type === 'Transit' ? 'TR' : 'PR');
                    if (hType === baseType) return; // 異なる技法を要求

                    const dayDiff = Math.abs(h.date.getTime() - row.dateObj.getTime()) / 86400000;
                    const combinedKey = [baseType, hType].sort().join('-'); // 'SA-TR', 'SA-PR', 'TR-PR'
                    
                    if (ECHO_WINDOWS[combinedKey] && dayDiff <= ECHO_WINDOWS[combinedKey]) {
                        targetScores[target] = (targetScores[target] || 0) + ECHO_WEIGHT[combinedKey];
                    }
                });
            });

            const totalScore = Object.values(targetScores).reduce((a, b) => a + b, 0);
            row.echoScore = totalScore;
            row.targetScores = targetScores;
            row.hasEcho = totalScore >= 3;
            row.hasStrongEcho = totalScore >= 6;
            
            Object.keys(targetScores).forEach(t => {
                if (targetScores[t] >= 1) row.echoTargets.add(t);
            });

            const filledColumns = (row.saHits.length > 0 ? 1 : 0) +
                (row.transitHits.length > 0 ? 1 : 0) +
                (row.progressHits.length > 0 ? 1 : 0);
            row.hasDensity = filledColumns >= 2;
        });

        return rows;
    }, [natalDate, transitDate, natalPositions, offsetYears]);

    const totalPages = Math.ceil(timelineData.length / ITEMS_PER_PAGE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return timelineData.slice(start, start + ITEMS_PER_PAGE);
    }, [timelineData, currentPage]);

    const handleCopyCsv = useCallback(() => {
        let csv = "Date,Solar Arc,Transit,Secondary Progress,Echo Targets,Density\n";
        timelineData.forEach(row => {
            const sa = row.saHits.map(h => h.isIndirect ? `${h.p2}/${h.p3}=${h.p1}` : `${h.p1}-${h.asp}-${h.p2}`).join('; ');
            const tr = row.transitHits.map(h => `${h.p1}${h.isRx ? 'Rx' : ''}-${h.asp}-${h.p2}`).join('; ');
            const pr = row.progressHits.map(h => `${h.p1}-${h.asp}-${h.p2}`).join('; ');
            const targets = Array.from(row.echoTargets).join(' ');
            const density = row.hasDensity ? "DENSITY" : "";
            csv += `"${row.date}","${sa}","${tr}","${pr}","${targets}","${density}"\n`;
        });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `predict_timeline_${new Date().toISOString().slice(0, 10)}.csv`);
        link.click();
    }, [timelineData]);

    const handleCopyText = useCallback(() => {
        let text = "ダイナミック予測タイムライン\n\n";
        timelineData.forEach(row => {
            text += `[${row.date}] `;
            if (row.hasEcho) text += `(ECHO: ${Array.from(row.echoTargets).join(', ')}) `;
            if (row.hasDensity) text += `(DENSITY) `;
            text += `\n`;
            if (row.saHits.length > 0) text += ` SA: ${row.saHits.map(h => h.isIndirect ? `${h.p2}/${h.p3}=${h.p1}` : `${h.p1}-${h.asp}-${h.p2}`).join(', ')}\n`;
            if (row.transitHits.length > 0) text += ` TR: ${row.transitHits.map(h => `${h.p1}${h.isRx ? 'Rx' : ''}-${h.asp}-${h.p2}`).join(', ')}\n`;
            if (row.progressHits.length > 0) text += ` PR: ${row.progressHits.map(h => `${h.p1}-${h.asp}-${h.p2}`).join(', ')}\n`;
            text += "\n";
        });
        navigator.clipboard.writeText(text);
        alert("コピーしました");
    }, [timelineData]);

    // ★修正: Dial90Chart 風のボタンスタイル
    const headerButtonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: "500",
        background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
        border: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
        color: isDarkMode ? "#cbd5e1" : "#64748b",
        cursor: "pointer",
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                {/* タイムラインヘッダー */}
                <div style={Object.assign({}, commonStyles.dynamicBlockTitle(isDarkMode, isMobile), {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    flexWrap: "wrap",
                    gap: "10px",
                    borderBottom: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                    paddingBottom: "10px",
                    marginBottom: "15px"
                })}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Zap size={20} color={isDarkMode ? "#ffffff" : "#f1c40f"} fill={isDarkMode ? "none" : "#f1c40f"} />
                        <span>ダイナミック予測タイムライン</span>
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={handleCopyText} style={headerButtonStyle}>
                            <Copy size={14} />
                            {!isMobile && "コピー"}
                        </button>
                        <button onClick={handleCopyCsv} style={headerButtonStyle}>
                            <Database size={14} />
                            {!isMobile && "CSV出力"}
                        </button>
                    </div>
                </div>

                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                        <thead>
                            <tr style={{ background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9", fontSize: "12px", color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                                <th style={{ padding: "12px 16px", textAlign: "left", width: "140px" }}>日付</th>
                                <th style={{ padding: "12px 16px", textAlign: "left" }}>ソーラーアーク</th>
                                <th style={{ padding: "12px 16px", textAlign: "left" }}>トランジット</th>
                                <th style={{ padding: "12px 16px", textAlign: "left" }}>セカンダリー・プログレッション</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, idx) => (
                                <tr key={idx} style={{
                                    background: "transparent",
                                    borderBottom: "1px solid " + (isDarkMode ? "#334155" : "#f1f5f9"),
                                    transition: "background 0.2s",
                                    position: "relative"
                                }}>
                                    <td style={{
                                        padding: "12px 16px", fontSize: "12px", verticalAlign: "top",
                                        borderLeft: row.hasStrongEcho ? "6px solid #ef4444" : (row.hasEcho ? "4px solid #f59e0b" : (row.hasDensity ? "4px solid #8b5cf6" : "4px solid transparent")),
                                        background: row.hasStrongEcho ? (isDarkMode ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)") : (row.hasEcho ? (isDarkMode ? "rgba(245, 158, 11, 0.08)" : "rgba(245, 158, 11, 0.05)") : (row.hasDensity ? (isDarkMode ? "rgba(139, 92, 246, 0.08)" : "rgba(139, 92, 246, 0.05)") : "transparent"))
                                    }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ fontWeight: "800", color: isDarkMode ? "#cbd5e1" : "#1e293b", fontFamily: "monospace" }}>{row.date}</span>
                                                {row.isAPCritical && (
                                                    <span style={{ fontSize: "10px", background: "#ef4444", color: "#fff", padding: "0 4px", borderRadius: "4px", fontWeight: "900" }}>AP!</span>
                                                )}
                                            </div>
                                            
                                            <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                                                <span style={{ fontSize: "10px", color: "#888" }}>Score:</span>
                                                <span style={{ fontSize: "14px", fontWeight: "900", color: row.hasStrongEcho ? "#ef4444" : (row.hasEcho ? "#f59e0b" : "#888") }}>{row.echoScore}</span>
                                            </div>

                                            {row.hasEcho && (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                                    {Array.from(row.echoTargets).map(target => (
                                                        <div key={target} style={{ display: "flex", alignItems: "center", gap: "2px", background: (row.targetScores[target] >= 3 ? "#f59e0b" : "#94a3b8"), color: "#fff", padding: "1px 6px", borderRadius: "10px", fontSize: "9px", fontWeight: "900" }}>
                                                            <LinkIcon size={8} /> {target}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: "12px 16px", verticalAlign: "top", background: row.hasEcho ? (isDarkMode ? "rgba(245, 158, 11, 0.05)" : "rgba(245, 158, 11, 0.02)") : (row.hasDensity ? (isDarkMode ? "rgba(139, 92, 246, 0.05)" : "rgba(139, 92, 246, 0.02)") : "transparent") }}>
                                        {row.saHits.map((h, i) => (
                                            <PredictHitItem key={i} {...h} type="S" isDarkMode={isDarkMode} planetColors={planetColors} signColors={signColors} rx1={Astro.isRetrograde(h.p1, row.dateObj)} isMobile={isMobile} />
                                        ))}
                                    </td>
                                    <td style={{ padding: "12px 16px", verticalAlign: "top", background: row.hasEcho ? (isDarkMode ? "rgba(245, 158, 11, 0.05)" : "rgba(245, 158, 11, 0.02)") : (row.hasDensity ? (isDarkMode ? "rgba(139, 92, 246, 0.05)" : "rgba(139, 92, 246, 0.02)") : "transparent") }}>
                                        {row.transitHits.map((h, i) => (
                                            <PredictHitItem key={i} {...h} type="T" isDarkMode={isDarkMode} planetColors={planetColors} signColors={signColors} rx1={h.isRx} isMobile={isMobile} />
                                        ))}
                                    </td>
                                    <td style={{ padding: "12px 16px", verticalAlign: "top", background: row.hasEcho ? (isDarkMode ? "rgba(245, 158, 11, 0.05)" : "rgba(245, 158, 11, 0.02)") : (row.hasDensity ? (isDarkMode ? "rgba(139, 92, 246, 0.05)" : "rgba(139, 92, 246, 0.02)") : "transparent") }}>
                                        {row.progressHits.map((h, i) => (
                                            <PredictHitItem key={i} {...h} type="P" isDarkMode={isDarkMode} planetColors={planetColors} signColors={signColors} isMobile={isMobile} />
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {timelineData.length === 0 && (
                        <div style={{ padding: "40px", textAlign: "center", color: "#888", fontSize: "14px" }}>
                            重要な転機（Exact日）は見つかりませんでした。
                        </div>
                    )}
                </div>

                {/* タイムラインページネーション */}
                <div style={{
                    padding: "16px 20px",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                    borderTop: "1px solid " + (isDarkMode ? "#334155" : "#e2e8f0"),
                    background: isDarkMode ? "rgba(0,0,0,0.1)" : "#fafafa",
                    marginTop: "10px",
                    borderRadius: "0 0 16px 16px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <button
                            onClick={() => { setOffsetYears(p => p - POOL_YEARS); setCurrentPage(1); }}
                            style={{ padding: "6px 12px", borderRadius: "6px", fontSize: "11px", cursor: "pointer", background: isDarkMode ? "#334155" : "#fff", border: "1px solid #cbd5e1", color: isDarkMode ? "#fff" : "#475569" }}
                        >
                            前の{POOL_YEARS}年
                        </button>
                        <span style={{ fontSize: "12px", color: isDarkMode ? "#94a3b8" : "#64748b", fontWeight: "bold" }}>
                            Page {currentPage} / {totalPages || 1} ({timelineData.length} hits)
                        </span>
                        <button
                            onClick={() => { setOffsetYears(p => p + POOL_YEARS); setCurrentPage(1); }}
                            style={{ padding: "6px 12px", borderRadius: "6px", fontSize: "11px", cursor: "pointer", background: isDarkMode ? "#334155" : "#fff", border: "1px solid #cbd5e1", color: isDarkMode ? "#fff" : "#475569" }}
                        >
                            次の{POOL_YEARS}年
                        </button>
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            style={{
                                padding: "8px 16px", borderRadius: "6px", fontSize: "12px", cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                background: isDarkMode ? "#334155" : "#fff", border: "1px solid #cbd5e1", color: isDarkMode ? "#fff" : "#475569",
                                opacity: currentPage === 1 ? 0.5 : 1, display: "flex", alignItems: "center", gap: "4px"
                            }}
                        >
                            <ChevronUp size={14} style={{ transform: "rotate(-90deg)" }} /> 前の100件
                        </button>
                        <button
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            style={{
                                padding: "8px 16px", borderRadius: "6px", fontSize: "12px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                                background: isDarkMode ? "#334155" : "#fff", border: "1px solid #cbd5e1", color: isDarkMode ? "#fff" : "#475569",
                                opacity: currentPage >= totalPages ? 0.5 : 1, display: "flex", alignItems: "center", gap: "4px"
                            }}
                        >
                            次の100件 <ChevronUp size={14} style={{ transform: "rotate(90deg)" }} />
                        </button>
                    </div>
                </div>

                <div style={{ padding: "12px 20px", background: isDarkMode ? "rgba(0,0,0,0.2)" : "#f8fafc", fontSize: "11px", color: "#888", textAlign: "right" }}>
                    ※ 各10年間のスパンでオーブが最小 (0.00) になる Exact 日を表示しています。
                </div>
            </div>

            {/* SA × Natal アスペクト・リスト */}
            {renderAspectList()}

            {/* SA × Natal アスペクト・グリッド (行列形式) */}
            {renderAspectMatrix()}
        </div>
    );
}
